import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

/**
 * MetricCard — dashboard-scoped KPI tile.
 *
 * Preserves the exact visual output of the inline `StatCard` helper that
 * lived inside `DashboardPage.jsx` prior to Phase 20.2. Larger, more
 * imperative than `ds/StatCard`: a big colored icon square on the right,
 * a 3xl value, and an optional "View all →" affordance.
 *
 * Migration to `ds/StatCard` (smaller, chip-style icon) is deferred to a
 * later phase because it constitutes a visual change and Phase 20.2 is
 * refactor-only.
 */
export const MetricCard = ({
  icon: Icon,
  value,
  label,
  color = 'bg-slate-900',
  href,
  loading = false,
  testId,
}) => {
  // Deep (navy-ish) vs accent tiles — used to map onto the active seasonal
  // chrome so dashboard icons feel part of the celebration. Default (no
  // active theme) keeps the original Tailwind colour.
  const tone = /slate-900|navy|zinc-900|gray-900|neutral-900/.test(color) ? 'deep' : 'accent';
  return (
  <Card className="card-hover animate-slideIn" data-testid={testId}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-3xl font-heading font-semibold text-slate-900">
            {loading ? '-' : (value ?? 0)}
          </p>
          <p className="text-sm text-slate-500 mt-1">{label}</p>
        </div>
        {Icon && (
          <div className={`p-3 rounded-lg kr-metric-icon ${color}`} data-tone={tone}>
            <Icon size={24} className="text-white" />
          </div>
        )}
      </div>
      {href && (
        <Link
          to={href}
          className="kr-metric-link mt-4 text-sm font-medium text-sky-600 hover:text-sky-700 flex items-center gap-1"
        >
          View all <ArrowRight size={14} />
        </Link>
      )}
    </CardContent>
  </Card>
  );
};

export default MetricCard;
